import { useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Eyebrow, rowLink } from "@/components/PageUi";
import { InfoHint } from "@/components/InfoHint";
import { RowPanel, type PanelRow } from "@/components/RowPanel";
import { expenseStateLabel, vatBlockedLabel } from "@/components/ExpenseStatus";
import { prettyCategory, prettyMerchant } from "@/components/ExpenseUi";
import type {
  ExpenseApprovalReport,
  ExpenseComplianceReport,
  ExpenseSpendReport,
  ExpenseVatReport,
  ReportState,
} from "@/hooks/useExpenseReports";

// The cards on the Expenses home. Every card reads one currency - the report's primary one - because amounts
// are never added across currencies; a mixed-currency organisation sees a note saying the rest exist.

export const num = (v: number | string | null | undefined): number =>
  typeof v === "number" ? v : v == null ? 0 : Number(v) || 0;

/** Whole-unit money for a glanceable dashboard. */
export function money(currency: string | null | undefined, amount: number | string | null | undefined): string {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency || "GBP",
      maximumFractionDigits: 0,
    }).format(num(amount));
  } catch {
    return `${Math.round(num(amount))}`;
  }
}

function inCurrency<T extends { currency?: string | null }>(rows: T[] | null | undefined, currency: string): T[] {
  return (rows ?? []).filter((r) => r.currency === currency);
}

function plural(n: number, one: string, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

function hours(h: number | string | null | undefined): string {
  if (h == null) return "-";
  const value = num(h);
  if (value < 1) return `${Math.max(1, Math.round(value * 60))} min`;
  if (value < 48) return `${value.toFixed(value < 10 ? 1 : 0)} h`;
  return `${Math.round(value / 24)} days`;
}

function waitedSince(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return days <= 0 ? "waiting since today" : `oldest waiting ${plural(days, "day")}`;
}

/** Each card loads and fails on its own, so one failing report never blanks the page. */
export function ReportCard({
  title,
  hint,
  state,
  children,
  className,
}: {
  title: string;
  hint?: ReactNode;
  state: Pick<ReportState<unknown>, "loading" | "error" | "retry"> & { data: unknown };
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("rounded-xl border bg-card/60 p-4 backdrop-blur-sm", className)}>
      <div className="mb-3 flex items-center gap-1.5">
        <Eyebrow>{title}</Eyebrow>
        {hint ? <InfoHint label={`More about ${title}`}>{hint}</InfoHint> : null}
      </div>
      {state.error ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm">
          <span className="text-destructive">{state.error}</span>
          <Button variant="outline" size="sm" onClick={state.retry}>
            Try again
          </Button>
        </div>
      ) : state.loading && !state.data ? (
        <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Spinner className="size-4" /> Loading...
        </p>
      ) : (
        <div className={cn(state.loading && "opacity-60 transition-opacity")}>{children}</div>
      )}
    </section>
  );
}

function Quiet({ children }: { children: ReactNode }) {
  return <p className="py-4 text-sm text-muted-foreground">{children}</p>;
}

/** A horizontal bar whose length is the row's share of the largest row. */
function Bar({ fraction, tone = "blue" }: { fraction: number; tone?: "blue" | "amber" | "muted" }) {
  return (
    <div className="h-2 overflow-hidden rounded-full bg-muted">
      <div
        className={cn(
          "h-full rounded-full",
          tone === "blue" && "bg-accent-blue",
          tone === "amber" && "bg-amber-500",
          tone === "muted" && "bg-muted-foreground/40",
        )}
        style={{ width: `${Math.max(fraction * 100, fraction > 0 ? 2 : 0)}%` }}
      />
    </div>
  );
}

function SegmentedBar({
  segments,
}: {
  segments: { key: string; label: string; value: number; display: string; className: string }[];
}) {
  const total = segments.reduce((s, x) => s + Math.max(x.value, 0), 0);
  if (total <= 0) return <Quiet>Nothing in this period.</Quiet>;
  return (
    <div>
      <div className="flex h-2.5 overflow-hidden rounded-full bg-muted">
        {segments.map((s) =>
          s.value > 0 ? (
            <div key={s.key} className={cn("h-full", s.className)} style={{ width: `${(s.value / total) * 100}%` }} />
          ) : null,
        )}
      </div>
      <ul className="mt-3 grid gap-1.5 text-sm">
        {segments.map((s) => (
          <li key={s.key} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2">
              <span className={cn("size-2 rounded-full", s.className)} />
              {s.label}
            </span>
            <span className="tabular-nums text-muted-foreground">{s.display}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const GRANULARITY_NOUN: Record<string, string> = { Daily: "day", Weekly: "week", Monthly: "month" };

export function SpendOverTimeCard({ state }: { state: ReportState<ExpenseSpendReport> }) {
  const report = state.data;
  const currency = report?.currency ?? "GBP";
  const buckets = inCurrency(report?.buckets, currency);
  const max = Math.max(...buckets.map((b) => num(b.total)), 0);
  const trend = inCurrency(report?.trend, currency)[0];
  const noun = GRANULARITY_NOUN[report?.granularity ?? "Weekly"] ?? "period";

  return (
    <ReportCard title="Spend over time" state={state}>
      {buckets.length === 0 || max <= 0 ? (
        <Quiet>No card spend in this period.</Quiet>
      ) : (
        <>
          <div className="flex h-40 items-end gap-1" role="img" aria-label={`Spend per ${noun}`}>
            {buckets.map((b) => {
              const value = num(b.total);
              const range =
                b.periodStart === b.periodEnd
                  ? formatDate(b.periodStart)
                  : `${formatDate(b.periodStart)} to ${formatDate(b.periodEnd)}`;
              return (
                <Tooltip key={`${b.periodStart}-${b.currency}`}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={`${range}: ${money(currency, value)}`}
                      className="group flex h-full min-w-0 flex-1 items-end focus-visible:outline-none"
                    >
                      <span
                        className={cn(
                          "w-full rounded-t-sm transition-colors",
                          value > 0
                            ? "bg-accent-blue/70 group-hover:bg-accent-blue group-focus-visible:bg-accent-blue"
                            : "bg-muted",
                        )}
                        style={{ height: `${value > 0 ? Math.max((value / max) * 100, 3) : 2}%` }}
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-xs">
                      <div className="font-semibold tabular-nums">{money(currency, value)}</div>
                      <div className="text-muted-foreground">
                        {range} · {plural(num(b.expenseCount), "expense")}
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
          <div className="mt-2 flex justify-between text-[11px] text-muted-foreground tabular-nums">
            <span>{formatDate(buckets[0]!.periodStart)}</span>
            <span>{formatDate(buckets[buckets.length - 1]!.periodEnd)}</span>
          </div>
          {trend ? (
            <p className="mt-3 text-xs text-muted-foreground">
              Previous period ({formatDate(trend.previousPeriodStart)} to {formatDate(trend.previousPeriodEnd)}):{" "}
              <span className="font-medium text-foreground tabular-nums">{money(currency, trend.previousTotal)}</span>
            </p>
          ) : null}
        </>
      )}
    </ReportCard>
  );
}

const FAMILY_LABELS: Record<string, string> = { Uncategorised: "Uncategorised" };

export function CategoryCard({ state }: { state: ReportState<ExpenseSpendReport> }) {
  const [open, setOpen] = useState<string | null>(null);
  const report = state.data;
  const currency = report?.currency ?? "GBP";
  const rows = inCurrency(report?.byCategory, currency).sort((a, b) => num(b.total) - num(a.total));
  const max = Math.max(...rows.map((r) => num(r.total)), 0);

  return (
    <ReportCard title="Where it went" hint="Grouped by the merchant's category. Your own category mappings name the rows inside each group." state={state}>
      {rows.length === 0 ? (
        <Quiet>No card spend in this period.</Quiet>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {rows.map((row) => {
            const family = row.family ?? "Uncategorised";
            const expanded = open === family;
            const labels = row.labels ?? [];
            const link = rowLink(labels.length > 1 ? () => setOpen(expanded ? null : family) : undefined);
            return (
              <li key={family}>
                <div {...link} className={cn("text-sm", labels.length > 1 && "cursor-pointer", link.className)}>
                  <div className="mb-1 flex items-center justify-between gap-3">
                    <span className={cn("flex items-center gap-1 font-medium", family === "Uncategorised" && "italic text-muted-foreground")}>
                      {FAMILY_LABELS[family] ?? family}
                      {labels.length > 1 ? (
                        <ChevronDown className={cn("size-3.5 text-muted-foreground transition-transform", expanded && "rotate-180")} />
                      ) : null}
                    </span>
                    <span className="tabular-nums">{money(currency, row.total)}</span>
                  </div>
                  <Bar fraction={max > 0 ? num(row.total) / max : 0} tone={family === "Uncategorised" ? "muted" : "blue"} />
                </div>
                {expanded ? (
                  <ul className="mt-2 ml-3 flex flex-col gap-1 border-l pl-3 text-xs text-muted-foreground">
                    {labels.map((l) => (
                      <li key={l.label} className="flex justify-between gap-3">
                        <span className="truncate">{prettyCategory(l.label)}</span>
                        <span className="tabular-nums">{money(currency, l.total)}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </ReportCard>
  );
}

export function TopMerchantsPanel({
  state,
  onOpenMerchant,
}: {
  state: ReportState<ExpenseSpendReport>;
  onOpenMerchant: (merchantId: string) => void;
}) {
  const report = state.data;
  const currency = report?.currency ?? "GBP";
  const rows: PanelRow[] = inCurrency(report?.topMerchants, currency)
    .slice(0, 6)
    .map((m, i) => ({
      key: m.merchantId ?? `merchant-${i}`,
      primary: (
        <span className="flex items-center gap-1.5">
          <span className="truncate">{prettyMerchant(m.name)}</span>
          {m.isNew ? (
            <Badge variant="outline" className="border-accent-blue/30 bg-accent-blue/10 px-1.5 py-0 text-[10px] text-accent-blue">
              New
            </Badge>
          ) : null}
        </span>
      ),
      secondary: plural(num(m.expenseCount), "expense"),
      right: money(currency, m.total),
      onOpen: m.merchantId ? () => onOpenMerchant(m.merchantId!) : undefined,
    }));
  return <PanelFor state={state} title="Top merchants" rows={rows} empty="No merchants in this period." />;
}

export function TopSpendersPanel({ state }: { state: ReportState<ExpenseSpendReport> }) {
  const report = state.data;
  const currency = report?.currency ?? "GBP";
  const rows: PanelRow[] = inCurrency(report?.bySpender, currency)
    .slice(0, 5)
    .map((s, i) => ({
      key: s.spender?.userId ?? `unassigned-${i}`,
      primary: s.spender ? s.spender.name || s.spender.email || "Unnamed" : <span className="italic text-muted-foreground">No card holder</span>,
      secondary: plural(num(s.expenseCount), "expense"),
      right: money(currency, s.total),
    }));
  return <PanelFor state={state} title="Top spenders" rows={rows} empty="No spend in this period." />;
}

export function CardsPanel({ state, onOpenCard }: { state: ReportState<ExpenseSpendReport>; onOpenCard: (id: string) => void }) {
  const report = state.data;
  const currency = report?.currency ?? "GBP";
  const rows: PanelRow[] = inCurrency(report?.byCard, currency)
    .slice(0, 5)
    .map((c) => ({
      key: c.cardConnectionId!,
      primary: c.displayName || "Card",
      secondary: c.last4 ? `ending ${c.last4} · ${plural(num(c.expenseCount), "expense")}` : plural(num(c.expenseCount), "expense"),
      right: money(currency, c.total),
      onOpen: () => onOpenCard(c.cardConnectionId!),
    }));
  return <PanelFor state={state} title="By card" rows={rows} empty="No card spend in this period." />;
}

function PanelFor({ state, title, rows, empty }: { state: ReportState<unknown>; title: string; rows: PanelRow[]; empty: string }) {
  if (state.error) return <ReportCard title={title} state={state}>{null}</ReportCard>;
  return <RowPanel title={title} rows={rows} loading={state.loading} empty={empty} />;
}

const LOCATION_META: Record<string, { label: string; className: string }> = {
  Domestic: { label: "At home", className: "bg-accent-blue" },
  Foreign: { label: "Abroad", className: "bg-accent-blue/40" },
  Unknown: { label: "Not known", className: "bg-muted-foreground/30" },
};

export function LocationCard({ state }: { state: ReportState<ExpenseSpendReport> }) {
  const report = state.data;
  const currency = report?.currency ?? "GBP";
  const rows = inCurrency(report?.byLocation, currency);
  return (
    <ReportCard title="Home and abroad" hint="Compares where the merchant is with your organisation's country." state={state}>
      <SegmentedBar
        segments={["Domestic", "Foreign", "Unknown"]
          .map((scope) => {
            const row = rows.find((r) => r.scope === scope);
            return {
              key: scope,
              label: LOCATION_META[scope]!.label,
              value: num(row?.total),
              display: money(currency, row?.total),
              className: LOCATION_META[scope]!.className,
            };
          })
          .filter((s) => s.key !== "Unknown" || s.value > 0)}
      />
    </ReportCard>
  );
}

const STATE_TAB: Record<string, string> = { PendingReview: "to-approve", Flagged: "attention", PostFailed: "attention" };

export function CloseReadinessCard({
  state,
  onOpenTab,
}: {
  state: ReportState<ExpenseComplianceReport>;
  onOpenTab: (tab: string) => void;
}) {
  const close = state.data?.closeReadiness;
  const rate = close?.postedRate == null ? null : Math.round(num(close.postedRate) * 100);
  const monthName = close?.monthStart
    ? new Date(close.monthStart).toLocaleString("en-GB", { month: "long", timeZone: "UTC" })
    : "Last month";
  const blocking = (close?.blocking ?? []).slice().sort((a, b) => num(b.count) - num(a.count));

  return (
    <ReportCard
      title={`${monthName} close`}
      hint="How much of last month's card spend is in your accounting platform, and what is holding up the rest."
      state={state}
    >
      {!close || num(close.expenseCount) === 0 ? (
        <Quiet>No card spend last month.</Quiet>
      ) : (
        <>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold tabular-nums tracking-tight">{rate ?? 0}%</span>
            <span className="text-sm text-muted-foreground">
              posted · {num(close.postedCount)} of {num(close.expenseCount)}
            </span>
          </div>
          <div className="mt-3">
            <Bar fraction={(rate ?? 0) / 100} tone={rate === 100 ? "blue" : "amber"} />
          </div>
          {blocking.length === 0 ? (
            <p className="mt-3 text-sm text-emerald-600">Everything from {monthName} is in the books.</p>
          ) : (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {blocking.map((b) => (
                <button
                  key={b.state}
                  type="button"
                  onClick={() => onOpenTab(STATE_TAB[b.state ?? ""] ?? "all")}
                  className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition hover:bg-muted/60"
                >
                  {expenseStateLabel(b.state)}
                  <span className="font-semibold tabular-nums">{num(b.count)}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </ReportCard>
  );
}

const AGE_LABELS: Record<string, string> = { Days0To7: "Under a week", Days8To30: "8 to 30 days", Days31Plus: "Over 30 days" };

export function ReceiptsCard({
  state,
  onOpenMissing,
}: {
  state: ReportState<ExpenseComplianceReport>;
  onOpenMissing: () => void;
}) {
  const report = state.data;
  const receipts = report?.receipts;
  const currency = report?.currency ?? "GBP";
  const coverage = receipts?.coverageRate == null ? null : Math.round(num(receipts.coverageRate) * 100);

  return (
    <ReportCard title="Receipts" state={state}>
      {!receipts || num(receipts.expenseCount) === 0 ? (
        <Quiet>No business spend in this period.</Quiet>
      ) : (
        <>
          <p className="text-sm">
            <span className="font-semibold tabular-nums">{coverage ?? 0}%</span>{" "}
            <span className="text-muted-foreground">of expenses have a receipt</span>
          </p>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {(receipts.aging ?? []).map((a) => {
              const amount = inCurrency(a.byCurrency, currency)[0]?.amount;
              const late = a.bucket === "Days31Plus" && num(a.count) > 0;
              return (
                <div key={a.bucket} className="rounded-lg border px-2.5 py-2">
                  <p className="text-[10px] text-muted-foreground">{AGE_LABELS[a.bucket ?? ""] ?? a.bucket}</p>
                  <p className={cn("text-lg font-bold tabular-nums", late && "text-amber-600")}>{num(a.count)}</p>
                  <p className="text-[11px] text-muted-foreground tabular-nums">{money(currency, amount)}</p>
                </div>
              );
            })}
          </div>
          {(receipts.bySpender ?? []).length > 0 ? (
            <>
              <Eyebrow className="mt-4 mb-1">Most to chase</Eyebrow>
              <ul className="flex flex-col text-sm">
                {(receipts.bySpender ?? []).slice(0, 4).map((s, i) => (
                  <li key={s.spender?.userId ?? `none-${i}`} className="flex justify-between gap-3 py-1">
                    <span className={cn("truncate", !s.spender && "italic text-muted-foreground")}>
                      {s.spender ? s.spender.name || s.spender.email : "No card holder"}
                    </span>
                    <span className="tabular-nums text-muted-foreground">{plural(num(s.missingCount), "receipt")}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {num(receipts.missingCount) > 0 ? (
            <Button variant="outline" size="sm" className="mt-3" onClick={onOpenMissing}>
              See missing receipts
            </Button>
          ) : null}
        </>
      )}
    </ReportCard>
  );
}

export function VatCard({ state }: { state: ReportState<ExpenseVatReport> }) {
  const report = state.data;
  const currency = report?.currency ?? "GBP";
  const reclaimable = inCurrency(report?.reclaimable, currency)[0];
  const blocked = inCurrency(report?.blocked, currency).sort((a, b) => num(b.grossSpend) - num(a.grossSpend));
  const atRisk = inCurrency(report?.estimatedVatAtRisk?.amounts, currency)[0];
  const max = Math.max(...blocked.map((b) => num(b.grossSpend)), 0);

  return (
    <ReportCard title="VAT" state={state}>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] text-muted-foreground">Reclaimable</p>
          <p className="text-xl font-bold tabular-nums">{money(currency, reclaimable?.amount)}</p>
        </div>
        <div>
          <p className="flex items-center gap-1 text-[10px] text-muted-foreground">
            At risk (estimate)
            <InfoHint label="More about VAT at risk">
              Spend with no receipt in categories where VAT is normally reclaimable, at your country's standard
              rate. A receipt would let you claim it.
            </InfoHint>
          </p>
          <p className={cn("text-xl font-bold tabular-nums", num(atRisk?.amount) > 0 && "text-amber-600")}>
            {report?.estimatedVatAtRisk ? `≈ ${money(currency, atRisk?.amount)}` : "-"}
          </p>
        </div>
      </div>
      {blocked.length > 0 ? (
        <>
          <Eyebrow className="mt-4 mb-2">Spend with VAT you can't claim</Eyebrow>
          <ul className="flex flex-col gap-2 text-sm">
            {blocked.map((b) => (
              <li key={b.reason}>
                <div className="mb-1 flex justify-between gap-3">
                  <span>{vatBlockedLabel(b.reason)}</span>
                  <span className="tabular-nums text-muted-foreground">{money(currency, b.grossSpend)}</span>
                </div>
                <Bar
                  fraction={max > 0 ? num(b.grossSpend) / max : 0}
                  tone={b.reason === "NoReceipt" || b.reason === "NoSupplierVatNumber" ? "amber" : "muted"}
                />
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </ReportCard>
  );
}

const OUTCOME_META: Record<string, { label: string; className: string }> = {
  AutoApprove: { label: "Approved by a policy", className: "bg-accent-blue" },
  RequireApproval: { label: "Needed approval", className: "bg-amber-500" },
  Flag: { label: "Flagged by a policy", className: "bg-destructive/70" },
  NoPolicy: { label: "No policy matched", className: "bg-muted-foreground/30" },
};

export function ApprovalsCard({ state }: { state: ReportState<ExpenseApprovalReport> }) {
  const report = state.data;
  const pending = report?.pending;
  const time = report?.timeToApprove;
  const policies = (report?.byPolicy ?? []).slice(0, 4);

  return (
    <ReportCard title="Approvals" state={state}>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] text-muted-foreground">Waiting now</p>
          <p className="text-xl font-bold tabular-nums">{num(pending?.count)}</p>
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground">Typical time to approve</p>
          <p className="text-xl font-bold tabular-nums">{num(time?.reviewedCount) > 0 ? hours(time?.medianHours) : "-"}</p>
          {num(time?.reviewedCount) > 0 ? (
            <p className="text-[11px] text-muted-foreground">
              slowest 10% over {hours(time?.p90Hours)} · {plural(num(time?.reviewedCount), "approval")}
            </p>
          ) : null}
        </div>
      </div>

      {(pending?.byApprover ?? []).length > 0 ? (
        <>
          <Eyebrow className="mt-4 mb-1">Waiting on</Eyebrow>
          <ul className="flex flex-col text-sm">
            {(pending?.byApprover ?? []).slice(0, 4).map((a, i) => (
              <li key={a.approver?.userId ?? `anyone-${i}`} className="flex justify-between gap-3 py-1">
                <span className="min-w-0">
                  <span className="block truncate">{a.approver ? a.approver.name || a.approver.email : "Any approver"}</span>
                  <span className="block text-xs text-muted-foreground">{waitedSince(a.oldestWaitingSince)}</span>
                </span>
                <span className="tabular-nums text-muted-foreground">{num(a.count)}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <Eyebrow className="mt-4 mb-2">What decided each expense</Eyebrow>
      <SegmentedBar
        segments={(report?.outcomes ?? []).map((o) => ({
          key: o.outcome ?? "NoPolicy",
          label: OUTCOME_META[o.outcome ?? "NoPolicy"]?.label ?? o.outcome ?? "",
          value: num(o.count),
          display: String(num(o.count)),
          className: OUTCOME_META[o.outcome ?? "NoPolicy"]?.className ?? "bg-muted",
        }))}
      />

      {policies.length > 0 ? (
        <>
          <Eyebrow className="mt-4 mb-1">Policies that matched most</Eyebrow>
          <ul className="flex flex-col text-sm">
            {policies.map((p) => (
              <li key={p.policyId} className="flex justify-between gap-3 py-1">
                <span className="truncate">{p.name ?? "Deleted policy"}</span>
                <span className="tabular-nums text-muted-foreground">{num(p.matchCount)}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {(report?.violationsBySpender ?? []).length > 0 ? (
        <>
          <Eyebrow className="mt-4 mb-1">Policy breaches</Eyebrow>
          <ul className="flex flex-col text-sm">
            {(report?.violationsBySpender ?? []).slice(0, 3).map((v, i) => (
              <li key={v.spender?.userId ?? `none-${i}`} className="flex justify-between gap-3 py-1">
                <span className={cn("truncate", !v.spender && "italic text-muted-foreground")}>
                  {v.spender ? v.spender.name || v.spender.email : "No card holder"}
                </span>
                <span className="tabular-nums text-muted-foreground">{num(v.count)}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </ReportCard>
  );
}
